import React from 'react';
import { Query } from 'react-apollo';
import { Card, CardContent, Typography } from '@material-ui/core';
import ListViewLayout from 'shared/layout/ListViewLayout';
import { UPLOAD_STATUS_SUMMARY_QUERY } from 'upload-status/queries';
import { SummaryQueryResult } from 'upload-status/types';
import SearchBar, { SearchBarQueryVars } from 'upload-status/components/SearchBar';
import UploadLogsTable from 'upload-status/components/UploadLogsTable';

const SUMMARY_CARDS = [
  { label: 'Pending', value: 'pending', color: '#2196f3', selectedColor: '#1565c0', borderColor: '#0d47a1' },
  { label: 'Importing', value: 'importing', color: '#fdd835', selectedColor: '#f9a825', borderColor: '#f57f17' },
  { label: 'Processing', value: 'processing', color: '#ff9800', selectedColor: '#e65100', borderColor: '#bf360c' },
  { label: 'Ready', value: 'ready', color: '#4caf50', selectedColor: '#2e7d32', borderColor: '#1b5e20' },
  { label: 'Failed', value: 'failed', color: '#f44336', selectedColor: '#b71c1c', borderColor: '#7f0000' },
];

interface State {
  submittedVars: SearchBarQueryVars | null;
  selectedSummaryStatuses: string[];
  queryKey: number;
  summaryKey: number;
}

class UploadStatusPage extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      submittedVars: null,
      selectedSummaryStatuses: [],
      queryKey: 0,
      summaryKey: 0,
    };
  }

  handleSearch = (vars: SearchBarQueryVars) => {
    this.setState((prev) => ({
      submittedVars: vars,
      selectedSummaryStatuses: [],
      queryKey: prev.queryKey + 1,
    }));
  };

  handleRefresh = () => {
    this.setState((prev) => ({ summaryKey: prev.summaryKey + 1 }));
  };

  render() {
    const { submittedVars, selectedSummaryStatuses, queryKey, summaryKey } =
      this.state;

    return (
      <ListViewLayout>
        <SearchBar onSearch={this.handleSearch} />

        {submittedVars && (
          <React.Fragment key={queryKey}>
            <Query<SummaryQueryResult>
              key={`summary-${summaryKey}`}
              query={UPLOAD_STATUS_SUMMARY_QUERY}
              variables={submittedVars}
              fetchPolicy="cache-and-network"
            >
              {({ data, error, loading }) => {
                if (!data && loading) return null;
                if (error)
                  return <pre style={{ color: 'red' }}>{error.message}</pre>;
                if (!data) return null;

                const summary = data.uploadStatusSummary;
                const items = SUMMARY_CARDS.map((card) => ({
                  ...card,
                  count: summary[card.value as keyof typeof summary],
                }));

                return (
                  <div
                    style={{ marginBottom: 32, opacity: loading ? 0.6 : 1 }}
                  >
                    <div style={{ display: 'flex', gap: 16, paddingTop: 16 }}>
                      <Card
                        style={{
                          minWidth: 120,
                          textAlign: 'center',
                          backgroundColor:
                            selectedSummaryStatuses.length === 0
                              ? '#555'
                              : '#999',
                          border:
                            selectedSummaryStatuses.length === 0
                              ? '2px solid #333'
                              : '2px solid transparent',
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          this.setState({ selectedSummaryStatuses: [] })
                        }
                      >
                        <CardContent style={{ padding: 16 }}>
                          <Typography variant="h4" style={{ color: '#fff' }}>
                            {summary.pending +
                              summary.importing +
                              summary.processing +
                              summary.ready +
                              summary.failed}
                          </Typography>
                          <Typography
                            variant="caption"
                            style={{ color: 'rgba(255,255,255,0.85)' }}
                          >
                            All
                          </Typography>
                        </CardContent>
                      </Card>
                      {items.map((item) => (
                        <Card
                          key={item.label}
                          style={{
                            minWidth: 120,
                            textAlign: 'center',
                            backgroundColor:
                              selectedSummaryStatuses.includes(item.value)
                                ? item.selectedColor
                                : item.color,
                            border: selectedSummaryStatuses.includes(item.value)
                              ? `2px solid ${item.borderColor}`
                              : '2px solid transparent',
                            cursor:
                              item.count > 0 ||
                              selectedSummaryStatuses.includes(item.value)
                                ? 'pointer'
                                : 'default',
                          }}
                          onClick={() => {
                            const isSelected =
                              selectedSummaryStatuses.includes(item.value);
                            if (item.count === 0 && !isSelected) return;
                            this.setState((prev) => ({
                              selectedSummaryStatuses:
                                prev.selectedSummaryStatuses.includes(
                                  item.value,
                                )
                                  ? prev.selectedSummaryStatuses.filter(
                                      (s) => s !== item.value,
                                    )
                                  : [
                                      ...prev.selectedSummaryStatuses,
                                      item.value,
                                    ],
                            }));
                          }}
                        >
                          <CardContent style={{ padding: 16 }}>
                            <Typography variant="h4" style={{ color: '#fff' }}>
                              {item.count}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ color: 'rgba(255,255,255,0.85)' }}
                            >
                              {item.label}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              }}
            </Query>

            <UploadLogsTable
              variables={submittedVars}
              selectedStatuses={selectedSummaryStatuses}
              queryKey={`${selectedSummaryStatuses.join(',')}-${summaryKey}`}
              onRefresh={this.handleRefresh}
            />
          </React.Fragment>
        )}
      </ListViewLayout>
    );
  }
}

export default UploadStatusPage;
